import React, { createContext, useContext, useState } from 'react'
import * as Sentry from '@sentry/gatsby'
import {
  persistPersonalData,
  removePersonalData,
  configureClientsWithPersonalData
} from '../analytics'
import { fetchWitRetry } from '../utils'
import { authenticateCommands } from '../logic/auth'

const AuthContext = createContext()

const API = {
  me: async function () {
    const response = await fetchWitRetry('/backend/wp-json/karinnarts/v1/me')
    if (response.status === 401) return null
    if (response.status !== 200) {
      const text = await response.text()
      Sentry.captureException(
        new Error(`${response.status} ${response.statusText} ${text}`)
      )
      return null
    }
    return await response.json()
  },
  courses: async function () {
    const response = await fetchWitRetry(
      '/backend/wp-json/karinnarts/v1/courses'
    )
    if (response.status === 401) return null
    if (response.status !== 200) {
      const text = await response.text()
      Sentry.captureException(
        new Error(`${response.status} ${response.statusText} ${text}`)
      )
      return null
    }
    return await response.json()
  },
  sendCode: async function (body) {
    let result = { isSuccess: false }
    try {
      const response = await fetchWitRetry(
        '/backend/wp-json/karinnarts/v1/auth/send-code',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      )
      if (response.status === 200) {
        result = await response.json()
      } else {
        Sentry.captureException(
          new Error(
            `${response.status} ${response.statusText} ${await response.text()}`
          )
        )
      }
    } catch (e) {
      Sentry.captureException(e)
    }
    return result
  },
  verifyCode: async function (body) {
    let result = { isSuccess: false }
    try {
      const response = await fetchWitRetry(
        '/backend/wp-json/karinnarts/v1/auth/verify',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      )
      if (response.status === 200) {
        result = await response.json()
      } else {
        Sentry.captureException(
          new Error(
            `${response.status} ${response.statusText} ${await response.text()}`
          )
        )
      }
    } catch (e) {
      Sentry.captureException(e)
    }
    return result
  },
  logout: async function () {
    let result = { isSuccess: false }
    try {
      const response = await fetchWitRetry(
        '/backend/wp-json/karinnarts/v1/logout',
        {
          method: 'POST'
        }
      )
      if (response.status === 200) {
        result = await response.json()
      } else {
        Sentry.captureException(
          new Error(
            `${response.status} ${response.statusText} ${await response.text()}`
          )
        )
      }
    } catch (e) {
      Sentry.captureException(e)
    }
    return result
  }
}

const INITIAL_NEXT_CHALLENGE = {
  course: null,
  isRegistered: false,
  isShowRegistration: false,
  isShowLink: false,
  date: null,
  day: null,
  time: null,
  displayDateTime: null,
  link: null
}

const INITIAL_NEXT_ZOOM = {
  isShowLink: false,
  date: null,
  day: null,
  time: null,
  displayDateTime: null,
  link: null
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState()
  const [isAdmin, setIsAdmin] = useState(false)
  const [isInAcademy, setIsInAcademy] = useState(false)
  const [myCourses, setMyCourses] = useState([])
  const [otherCourses, setOtherCourses] = useState([])
  const [challengeCourses, setChallengeCourses] = useState([])
  const [sugarDoughCourses, setSugarDoughCourses] = useState([])
  const [artCourses, setArtCourses] = useState([])
  const [businessCourses, setBusinessCourses] = useState([])
  const [sculptureMethodCourse, setSculptureMethodCourse] = useState(null)
  const [nextChallengeCourse, setNextChallengeCourse] = useState(
    INITIAL_NEXT_CHALLENGE
  )
  const [nextZoomMeeting, setNextZoomMeeting] = useState(INITIAL_NEXT_ZOOM)
  const [enrolledCourses, setEnrolledCourses] = useState([])
  const [loading, setLoading] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  function runCommand(cmd) {
    switch (cmd.type) {
      case 'SET_STATE': {
        const p = cmd.payload
        if (p.user !== undefined) setUser(p.user)
        if (p.isAdmin !== undefined) setIsAdmin(p.isAdmin)
        if (p.isInAcademy !== undefined) setIsInAcademy(p.isInAcademy)
        if (p.loading !== undefined) setLoading(p.loading)
        break
      }
      case 'SET_COURSES': {
        const c = cmd.payload
        setEnrolledCourses(c.enrolledCourses)
        setMyCourses(c.myCourses)
        setOtherCourses(c.otherCourses)
        setChallengeCourses(c.challengeCourses)
        setSugarDoughCourses(c.sugarDoughCourses)
        setArtCourses(c.artCourses)
        setBusinessCourses(c.businessCourses)
        setSculptureMethodCourse(c.sculptureMethodCourse)
        break
      }
      case 'SET_NEXT_ZOOM':
        setNextZoomMeeting(cmd.payload)
        break
      case 'SET_NEXT_CHALLENGE':
        setNextChallengeCourse(cmd.payload)
        break
      case 'ANALYTICS_CONFIGURE':
        configureClientsWithPersonalData(cmd.payload.userId, cmd.payload.email)
        break
      case 'ANALYTICS_PERSIST':
        persistPersonalData(cmd.payload)
        break
      case 'ANALYTICS_REMOVE':
        removePersonalData()
        break
      case 'RESET_STATE':
        setUser()
        setIsAdmin(false)
        setIsInAcademy(false)
        setMyCourses([])
        setOtherCourses([])
        setChallengeCourses([])
        setSugarDoughCourses([])
        setArtCourses([])
        setBusinessCourses([])
        setSculptureMethodCourse(null)
        setNextChallengeCourse(INITIAL_NEXT_CHALLENGE)
        setNextZoomMeeting(INITIAL_NEXT_ZOOM)
        setEnrolledCourses([])
        break
      default:
        break
    }
  }

  async function authenticate() {
    setLoading(true)
    try {
      const [meData, coursesData] = await Promise.all([API.me(), API.courses()])
      const commands = authenticateCommands(meData, coursesData)
      commands.forEach((cmd) => runCommand(cmd))
    } catch (e) {
      Sentry.captureException(e)
    } finally {
      setLoading(false)
      setIsLoaded(true)
    }
  }

  async function logout() {
    setLoading(true)
    await API.logout()
    runCommand({ type: 'RESET_STATE' })
    setLoading(false)
    removePersonalData()
  }

  const state = {
    user,
    isAdmin,
    isInAcademy,
    myCourses,
    otherCourses,
    challengeCourses,
    sugarDoughCourses,
    artCourses,
    businessCourses,
    sculptureMethodCourse,
    nextChallengeCourse,
    nextZoomMeeting,
    enrolledCourses,
    loading,
    isLoaded,
    authenticate,
    sendCode: API.sendCode,
    verifyCode: API.verifyCode,
    logout
  }

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
}

export default function useAuth() {
  return useContext(AuthContext)
}
